// src/edu-world/systems/npc-interaction.ts
// NPC proximity detection and activation (Phase 1)
// Detects player within 1.5 character-widths of NPC, emits events via EventBus.

import Phaser from 'phaser';
import type { NPCConfig } from '../types';
import type { EventBus } from '../core/event-bus';
import type { NPCRenderer } from './npc-renderer';
import type { PlayerController } from '../core/player-controller';

export class NPCInteraction {
  private scene: Phaser.Scene;
  private eventBus: EventBus;
  private npcRenderer: NPCRenderer;
  private playerController: PlayerController;
  private npcConfigs: NPCConfig[];

  // Track which NPCs are currently in range
  private inRange: Set<string> = new Set();

  constructor(
    scene: Phaser.Scene,
    eventBus: EventBus,
    npcRenderer: NPCRenderer,
    playerController: PlayerController,
    npcConfigs: NPCConfig[]
  ) {
    this.scene = scene;
    this.eventBus = eventBus;
    this.npcRenderer = npcRenderer;
    this.playerController = playerController;
    this.npcConfigs = npcConfigs;

    // Listen for pointer on NPC bodies (activation)
    this.scene.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      // Don't process if pointer hit an interactive UI element (buttons, etc.)
      const hitObjects = this.scene.input.hitTestPointer(pointer);
      if (hitObjects.length > 0) return; // An interactive object was clicked, skip NPC tap
      this.handleTap(pointer.worldX, pointer.worldY);
    });
  }

  /** Call every frame to check proximity */
  update(): void {
    const playerPos = this.playerController.getPosition();

    for (const npc of this.npcConfigs) {
      const distance = Phaser.Math.Distance.Between(
        playerPos.x,
        playerPos.y,
        npc.position.x,
        npc.position.y
      );

      const wasInRange = this.inRange.has(npc.id);
      const isNowInRange = distance <= npc.interaction_radius;

      if (isNowInRange && !wasInRange) {
        // Entered range
        this.inRange.add(npc.id);
        this.npcRenderer.showProximityIndicator(npc.id);
        this.eventBus.emit('player:proximity', {
          npc_id: npc.id,
          in_range: true,
          distance,
        });
      } else if (!isNowInRange && wasInRange) {
        // Left range
        this.inRange.delete(npc.id);
        this.npcRenderer.hideProximityIndicator(npc.id);
        this.eventBus.emit('player:proximity', {
          npc_id: npc.id,
          in_range: false,
          distance,
        });
      }
    }
  }

  /** Handle tap — check if it's on an NPC */
  private handleTap(worldX: number, worldY: number): void {
    for (const npc of this.npcConfigs) {
      const distToNPC = Phaser.Math.Distance.Between(
        worldX, worldY, npc.position.x, npc.position.y
      );

      // Tap hit detection: within 48px of NPC center (generous touch target)
      if (distToNPC > 48) continue;

      const playerPos = this.playerController.getPosition();
      const playerDistToNPC = Phaser.Math.Distance.Between(
        playerPos.x, playerPos.y, npc.position.x, npc.position.y
      );

      if (playerDistToNPC <= npc.interaction_radius) {
        // Player is in range — activate NPC
        this.eventBus.emit('player:activate', {
          npc_id: npc.id,
          player_position: playerPos,
        });
      } else {
        // Player is out of range — show encouraging beckon
        this.npcRenderer.playBeckonAnimation(npc.id);
      }
      return; // Only handle the first NPC hit
    }
  }

  /** Check if a specific NPC is currently in range */
  isInRange(npcId: string): boolean {
    return this.inRange.has(npcId);
  }
}
